import React, { useState } from "react";
import {
  useLocation,
  NavLink as NavLinkRRD,
  Link,
  useNavigate,
} from "react-router-dom";
import classnames from "classnames";
import { PropTypes } from "prop-types";
import {
  Collapse,
  NavbarBrand,
  NavItem,
  NavLink,
  Navbar,
  Nav,
} from "reactstrap";
import { hasModuleAccess } from "utils/permissions.js";
import { ThemeContext } from "contexts/ThemeContext"; // adjust path if needed
import { hasPermission } from "utils/permissions";
import "./css/Sidebar.css";
import classNames from "classnames";
import { getAccessibleRoutes } from "./routeFilter";

function Sidebar({ toggleSidenav, sidenavOpen, routes, logo, rtlActive }) {
  const { theme } = React.useContext(ThemeContext);
  const isDark = theme === "dark";
  const [userData, setUserData] = React.useState(null);
  const accessibleRoutes = getAccessibleRoutes(routes, userData);
  const [state, setState] = React.useState({});
  const [closedNav, setClosedNav] = React.useState(true);
  const [clientModules, setClientModules] = React.useState([]);
  const location = useLocation();
  const modulesList = ["hucs", "hrms", "mdm"];
  const initialModule = modulesList.find((module) =>
    window.location.pathname.includes(`/${module}/`),
  );
  const navigate = useNavigate();
  const [activeSubmenu, setActiveSub] = useState(
    initialModule ? initialModule.toUpperCase() : null,
  );
  const [hasActiveSubmenu, setHasActiveSubmenu] = useState(!!initialModule);
  const hasRouteAccess = (prop) => {
    // Dashboards always allowed
    if (prop.name === "Dashboards") return true;

    // Capability-based visibility (NEW)
    if (
      prop.requiredPermissions &&
      !prop.requiredPermissions.every((p) => hasPermission(userData, p))
    ) {
      return false;
    }

    // Module access (client-based)
    if (prop.name === "HRMS") {
      return hasModuleAccess(userData, clientModules, "HRMS");
    }

    if (prop.name === "HUCS") {
      return hasModuleAccess(userData, clientModules, "HUCS");
    }

    return true;
  };

  console.log("Active Submenu onclick ", activeSubmenu);

  const closeSidenav = () => {
    if (window.innerWidth < 1200) toggleSidenav();
  };

  const pathMatches = (route) => {
    if (route) {
      const splitRoute = route.split("/");
      const splitPath = location.pathname.split("/");
      return (
        splitPath.at(-1) === splitRoute.at(-1) &&
        splitPath.at(-2) === splitRoute.at(-2)
      );
    }
    return false;
  };
  // One-time initialization on mount
  React.useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const storedModules = localStorage.getItem("clientModules");
    if (storedUser) setUserData(JSON.parse(storedUser));
    if (storedModules) setClientModules(JSON.parse(storedModules));
  }, []);

  React.useEffect(() => {
    if (accessibleRoutes?.length > 0) {
      setState((prev) => {
        if (Object.keys(prev).length > 0) return prev;
        return getCollapseStates(accessibleRoutes);
      });
    }
  }, [accessibleRoutes]);

  const activeRoute = (routeName) =>
    location.pathname.indexOf(routeName) > -1 ? "active" : "";

  const isHomeRoute = (prop) => prop.layout + prop.path === "/admin/home";
  const renderIcon = (prop) => {
    if (!prop.icon) return null;

    const isHome = isHomeRoute(prop);

    return (
      <span className={classnames("sidebar-icon", { "home-icon": isHome })}>
        {prop.iconType === "image" ? (
          <img src={prop.icon} alt={prop.name} className="sidebar-icon-img" />
        ) : (
          <i className={prop.icon} />
        )}
      </span>
    );
  };
  const clicked = (module_name) => {
    console.log("Active submenu: ", activeSubmenu);
    if (module_name === activeSubmenu) {
      // close the sub menu!
      setActiveSub(null);
      setTimeout(() => {
        setHasActiveSubmenu(false);
      }, 350);
    } else {
      //check to see if we're already in a module!
      setActiveSub(module_name);
      setHasActiveSubmenu(true);
      navigate(`/admin/${module_name.toLowerCase()}/dashboard`);
    }
  };
  const getCollapseInitialState = (accessibleRoutes) => {
    for (let i = 0; i < accessibleRoutes.length; i++) {
      if (accessibleRoutes[i].collapse && getCollapseInitialState(accessibleRoutes[i].views)) {
        return true;
      } else if (pathMatches(accessibleRoutes[i].path)) {
        return true;
      }
    }
    return false;
  };

  const getCollapseStates = (accessibleRoutes) => {
    let initialState = {};
    accessibleRoutes.forEach((prop) => {
      if (prop.collapse) {
        initialState = {
          [prop.state]: getCollapseInitialState(prop.views),
          ...getCollapseStates(prop.views),
          ...initialState,
        };
      }
    });
    return initialState;
  };

  const closeSubMenus = () => {
    console.log("Global Clicked");
    console.log(activeSubmenu);
    setActiveSub(null);
    setTimeout(() => {
      setHasActiveSubmenu(false);
    }, 350);
    return;
  };

  const collaseSidenav = () => {
    toggleSidenav();
    setClosedNav(prev => !prev)
  }

  const modules_rail = React.useMemo(() => {
    const sidebar_images = accessibleRoutes.map((element) => {
      if (element.module)
        return (
          <NavLink
            className={classNames(
              activeSubmenu === element.name
                ? `active-submenu ${element.name.toLowerCase()}`
                : "",
              `icon-highlight`,
            )}
            onClick={() => clicked(element.name)}
          >
            <img
              src={element.icon}
              alt={element.name}
              className="sidebar-icon-img"
            />
            {!hasActiveSubmenu && (
              <span
                className={`module-name nav-link-text ${classNames(hasActiveSubmenu ? `active-submenu ${element.name.toLowerCase()}` : "")}`}
              >
                {element.name}
              </span>
            )}
          </NavLink>
        );
    });
    return sidebar_images;
  }, [accessibleRoutes, activeSubmenu, hasActiveSubmenu]);

  // helper function for handling the submodules and their dropdowns
  const createLinks = (accessibleRoutes) => {
    return accessibleRoutes.map((prop, key) => {
      if (prop.invisible) return null;
      // Capability-based visibility (primary)
      if (
        prop.requiredPermissions &&
        !prop.requiredPermissions.every((p) => hasPermission(userData, p))
      ) {
        return null;
      }

      // Legacy role-based visibility (optional)
      if (
        prop.allowedRoles &&
        !prop.allowedRoles.includes(userData?.user_role)
      ) {
        return null;
      }

      // Skip restricted or irrelevant paths
      const hiddenPaths = [
        "/dealer",
        "/calls-overview",
        "/dealer-menu",
        "/registrations-overview",
        "/companyusers",
        "/companyclients",
        "/companysites",
        "/companyprofile",
        "/profile",
        "/hrms/device",
        "/hrms/directory",
        "/siteprofile",
        "/login",
        "/hucs/system-status",
        "/hucs/access-controls",
        "/hucs/databases",
        "/hucs/default-settings",
        "/hucs/domains",
        "/hucs/email-templates",
        "/hucs/modules",
        "/hucs/number-translations",
        "/hucs/sip-profiles",
        "/hucs/variables",
        "/hucs/sips-status",
      ];
      // Hide route if user role is not explicitly allowed
      if (
        prop.allowedRoles &&
        !prop.allowedRoles.includes(userData?.user_role)
      ) {
        return null;
      }
      if (hiddenPaths.includes(prop.path)) return null;
      if (prop.redirect) return null;
      if (!hasRouteAccess(prop)) return null;

      // COLLAPSE LINKS
      if (prop.collapse) {
        return (
          <NavItem key={key}>
            <NavLink
              href="#pablo"
              data-toggle="collapse"
              aria-expanded={state[prop.state]}
              className={classnames(
                { active: state[prop.state] },
                `${prop.name.toLowerCase()}-active`,
              )}
              onClick={(e) => {
                e.preventDefault();

                setState((prev) => {
                  const newState = { ...prev };
                  const isOpening = !prev[prop.state];

                  // Close all sibling top-level modules
                  accessibleRoutes.forEach((r) => {
                    if (r.collapse && r.state !== prop.state) {
                      newState[r.state] = false;
                    }
                  });

                  newState[prop.state] = isOpening;

                  // HUCS-specific behavior
                  if (prop.parent === "HUCS" || prop.name === "HUCS") {
                    const hucs = accessibleRoutes.find((r) => r.name === "HUCS");
                    if (hucs?.views) {
                      hucs.views.forEach((child) => {
                        if (child.state !== prop.state) {
                          newState[child.state] = false;
                        }
                      });
                    }
                  }

                  return newState;
                });
              }}
              style={{
                paddingLeft: "2rem",
              }}
            >
              {renderIcon(prop)}
              <span className="nav-link-text">
                {prop.name}&nbsp;&nbsp;
                {state[prop.state] ? (
                  <i class="fas fa-angle-down"></i>
                ) : (
                  <i class="fas fa-angle-right"></i>
                )}
              </span>
            </NavLink>

            <Collapse
              isOpen={state[prop.state]}
              onClick={(e) => e.stopPropagation()}
            >
              <Nav className={`flex-column ${prop.name.toLowerCase()}`}>
                {createLinks(prop.views)}
              </Nav>
            </Collapse>
          </NavItem>
        );
      }

      // NORMAL LINKS
      return (
        <NavItem key={key}>
          <NavLink
            to={prop.layout + prop.path}
            className={
              isHomeRoute(prop)
                ? "home"
                : `route-link ${prop.path.includes("dashboard") ? "dashboard" : ""}`
            }
            tag={NavLinkRRD}
            style={{
              backgroundColor:
                isHomeRoute(prop) && activeRoute(prop.layout + prop.path)
                  ? "#3B4E68"
                  : undefined,
            }}
          >
            {renderIcon(prop)}
            <span
              className="nav-link-text"
              style={
                isHomeRoute(prop) && activeRoute(prop.layout + prop.path)
                  ? { color: "#f0f0f0" }
                  : undefined
              }
            >
              {prop.name}
            </span>
          </NavLink>
        </NavItem>
      );
    });
  };

  const sub_modules = React.useMemo(() => {
    return accessibleRoutes.map((element) => {
      if (!element.module) return null;
      return (
        <Collapse
          key={element.name}
          isOpen={activeSubmenu === element.name}
          className="sidebar"
        >
          <Nav className={`flex-column ${element.name.toLowerCase()}`}>
            {element.views ? createLinks(element.views) : null}
          </Nav>
        </Collapse>
      );
    });
  }, [accessibleRoutes, activeSubmenu, state, userData]);

  let navbarBrandProps;

  if (logo && logo.innerLink)
    navbarBrandProps = {
      to: logo.innerLink,
      tag: Link,
      onClick: closeSubMenus,
    };
  else if (logo && logo.outerLink)
    navbarBrandProps = {
      href: logo.outerLink,
      target: "_blank",
      onClick: closeSubMenus,
    };
  return (
    <Navbar
      className={classnames(
        "sidenav navbar-vertical navbar-expand-xs husaria-sidebar",
        rtlActive ? "" : "fixed-left",
        isDark ? "navbar-dark bg-dark" : "navbar-light bg-white",
        {
          "sidebar-collapsed": !sidenavOpen,
        },
      )}
    >
      <div className="non-collapsable-links">
        <div className="sidenav-header d-flex align-items-center">
          {logo && (
            <NavbarBrand {...navbarBrandProps}>
              <img
                alt={logo.imgAlt}
                className="navbar-brand-img"
                src={logo.imgSrc}
              />
            </NavbarBrand>
          )}
          <div className="ml-auto">
            <div
              className={classnames("sidenav-toggler d-none d-xl-block", {
                active: sidenavOpen,
              })}
              onClick={toggleSidenav}
            >
              <div className="sidenav-toggler-inner">
                <i
                  className="sidenav-toggler-line"
                  style={{ backgroundColor: isDark ? "#f0f0f0" : "#1F1F1F" }}
                />
                <i
                  className="sidenav-toggler-line"
                  style={{ backgroundColor: isDark ? "#f0f0f0" : "#1F1F1F" }}
                />
                <i
                  className="sidenav-toggler-line"
                  style={{ backgroundColor: isDark ? "#f0f0f0" : "#1F1F1F" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="home-link">
        <NavItem key={"home"}>
          <NavLink
            to={"/admin/home"}
            className={"home account-links"}
            tag={NavLinkRRD}
            onClick={(e) => {
              closeSidenav();
              closeSubMenus();
            }}
            style={{
              backgroundColor: activeRoute("/admin/home")
                ? "#3B4E68"
                : undefined,
            }}
          >
            <span className={classnames("sidebar-icon", "home-icon")}>
              <i className={"fas fa-home"} />
            </span>
            <span
              className="nav-link-text"
              style={{
                color: activeRoute("/admin/home") ? "#f0f0f0" : undefined,
              }}
            >
              Global Dashboard
            </span>
          </NavLink>
        </NavItem>
      </div>
      <div className="main-links-section">
        <div className="navigation-rail">{modules_rail}</div>
        <div
          className={`sub-menu ${activeSubmenu ? activeSubmenu.toLowerCase() : ""}`}
        >
          {sub_modules}
        </div>
      </div>
      <div className="non-collapsable-links">
        <div className="navbar-inner">
          <Collapse navbar isOpen={true}>
            <hr className="my-3" />
            <h6 className="navbar-heading p-0 text-muted">
              <span className="docs-normal">Account</span>
              <span className="docs-mini">
                <i className="fas fa-user" />
              </span>
            </h6>
            <Nav className="mb-md-3" navbar>
              {userData?.user_role === "GLOBAL" && (<NavItem>
                <NavLink
                  className="subscription account-links"
                  tag={NavLinkRRD}
                  to="/admin/subscription"
                  style={{
                    backgroundColor: activeRoute("/admin/subscription")
                      ? "#3B4E68"
                      : undefined,
                  }}
                >
                  <i
                    className="fas fa-layer-group"
                    style={{
                      color: activeRoute("/admin/subscription")
                        ? "#f0f0f0"
                        : undefined,
                    }}
                  />
                  <span
                    className="nav-link-text"
                    style={{
                      color: activeRoute("/admin/subscription")
                        ? "#f0f0f0"
                        : undefined,
                    }}
                  >
                    Subscription
                  </span>
                </NavLink>
              </NavItem>)}
              <NavItem>
                <NavLink
                  className="account-links"
                  href="https://docs.husariatech.com/en/husariaone"
                  target="_blank"
                  style={{}}
                >
                  <i className="fas fa-question-circle" />
                  <span className="nav-link-text">Wiki / Docs</span>
                </NavLink>
              </NavItem>
            </Nav>
          </Collapse>
        </div>
      </div>
    </Navbar>
  );
}

Sidebar.defaultProps = {
  routes: [{}],
  toggleSidenav: () => { },
  sidenavOpen: false,
  rtlActive: false,
};

Sidebar.propTypes = {
  toggleSidenav: PropTypes.func,
  sidenavOpen: PropTypes.bool,
  routes: PropTypes.arrayOf(PropTypes.object),
  logo: PropTypes.shape({
    innerLink: PropTypes.string,
    outterLink: PropTypes.string,
    imgSrc: PropTypes.string.isRequired,
    imgAlt: PropTypes.string.isRequired,
  }),
  rtlActive: PropTypes.bool,
};

export default Sidebar;
